import { Eye, EyeOff, MessageSquareText } from 'lucide-react';
import { buildStarFillStates, safeReviewDisplayName } from '@dukanti/shared';
import type { AdminProductReviewRow } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface ReviewsMobileCardsProps {
  readonly rows: readonly AdminProductReviewRow[];
  readonly canModerate: boolean;
  readonly onRespond: (review: AdminProductReviewRow) => void;
  readonly onVisibility: (review: AdminProductReviewRow) => void;
}

export function ReviewsMobileCards({
  rows,
  canModerate,
  onRespond,
  onVisibility,
}: ReviewsMobileCardsProps): React.JSX.Element {
  const { t, dir } = useTranslation();

  return (
    <div dir={dir} data-testid="reviews-mobile-cards" className="flex flex-col gap-3 md:hidden">
      {rows.map((row) => (
        <article
          key={row.id}
          className="border-border bg-card shadow-admin-card rounded-lg border p-4 text-start"
          data-testid="reviews-mobile-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-foreground truncate font-semibold">
                {row.product_name_snapshot}
              </h2>
              <p className="text-muted-foreground truncate text-xs">
                {row.variant_option_summary_snapshot}
              </p>
            </div>
            <Badge variant={row.visibility === 'visible' ? 'success' : 'warning'}>
              {t(`admin.reviews.visibility.${row.visibility}` as never)}
            </Badge>
          </div>
          <div className="text-saffron mt-3 inline-flex" dir="ltr" aria-hidden>
            {buildStarFillStates(row.rating).map((state, index) => (
              <span key={index}>{state === 'full' ? '★' : '☆'}</span>
            ))}
          </div>
          <p className="text-foreground mt-2 whitespace-pre-line break-words text-sm">
            {row.review_text ?? t('reviews.verifiedPurchase')}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {safeReviewDisplayName(row.customer_display_name) ?? t('reviews.verifiedCustomer')}
          </p>
          {row.hidden_reason ? (
            <p className="text-muted-foreground mt-2 text-xs">
              {t('admin.reviews.moderation.hiddenMeta', {
                reason: t(`admin.reviews.hideReasons.${reasonKey(row.hidden_reason)}` as never),
              })}
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canModerate}
              onClick={() => onRespond(row)}
            >
              <MessageSquareText className="size-4" aria-hidden />
              {row.merchant_response_text
                ? t('admin.reviews.actions.editResponse')
                : t('admin.reviews.actions.respond')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canModerate}
              onClick={() => onVisibility(row)}
            >
              {row.visibility === 'visible' ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
              {row.visibility === 'visible'
                ? t('admin.reviews.actions.hide')
                : t('admin.reviews.actions.unhide')}
            </Button>
          </div>
        </article>
      ))}
    </div>
  );
}

function reasonKey(reason: AdminProductReviewRow['hidden_reason']): string {
  switch (reason) {
    case 'private_info':
      return 'privateInfo';
    case 'off_topic':
      return 'offTopic';
    case 'abusive':
    case 'spam':
    case 'other':
      return reason;
    default:
      return 'other';
  }
}
